import { useState, useEffect, useMemo, useCallback } from 'react';
import isEqual from 'lodash/isEqual';
import cloneDeep from 'lodash/cloneDeep';
import flattenDeep from 'lodash/flattenDeep';
import { encodeSelections, decodeSelections } from '@/utils/widget/parseSelections';
import { spreadsheet as spreadsheetCore } from '@industriousapps/excelkits-core';
const SELECTION_INDEX_QUERY = 'data-spreadsheet-selection-index'; // const PREFIX =
//   'features: WidgetEdit: modals: SpreadsheetSelections: useSelections:';

export default function useSelections(_ref) {
  let {
    containerEl,
    initialSelections,
    initialSelectionIndex = -1,
    labeled,
    workbook,
    maxCollections,
    isMultiCollectionSelection,
    selectionLimits,
    maxSelections,
    selectionsStrategy
  } = _ref;
  const {
    0: updatedAt,
    1: setUpdatedAt
  } = useState(0); // Triggers publishable recalcuation

  const {
    0: selections,
    1: setSelections
  } = useState(initialSelections); // user facing

  const {
    0: publishableSelections,
    1: setPublishableSelections
  } = useState(initialSelections); // system
  // TODO: support deeply nested selections
  //       already suppored by parsing

  const {
    0: collections,
    1: setCollections
  } = useState(decodeSelections(initialSelections)); // Lookup initial collection index

  const {
    0: activeCollection,
    1: setActiveCollection
  } = useState(() => {
    return getInitialCollectionIndex(collections, initialSelectionIndex);
  }); // Automatically start editing selections
  // if current selections are less than max

  const {
    0: isAddingNewSelection,
    1: setIsAddingNewSelection
  } = useState(() => {
    // Do not auto-activate if initial selection is set
    if (initialSelectionIndex !== -1) return false;
    const selectionsCount = collections.reduce((acc, selections) => {
      acc += selections.length;
      return acc;
    }, 0);
    return selectionsCount < maxSelections;
  });
  const {
    0: activeSelection,
    1: setActiveSelection
  } = useState(initialSelectionIndex);
  const {
    0: invalidSelections,
    1: setInvalidSelections
  } = useState(workbook.length ? filterInvalidCollectionSelections() : []); // TODO: support deeply nested selections

  const activeSelectionCellRef = useMemo(() => {
    if (activeSelection === -1) return ''; // adding new selection

    return (collections[activeCollection] || [])[activeSelection] || flattenDeep(collections)[initialSelectionIndex] || '';
  }, [collections, activeCollection, initialSelectionIndex, activeSelection]);
  const hasUpdates = useMemo(() => {
    return !isEqual(initialSelections, selections) && updatedAt > 0;
  }, [initialSelections, selections, updatedAt]);
  const customLabeledSelectionLimitKey = `${selectionsStrategy || ''}${labeled ? 'labeled' : ''}`;
  const activeSelectionLimit = selectionLimits[customLabeledSelectionLimitKey] || // labeled custom
  selectionLimits[selectionsStrategy] || // custom
  selectionLimits.default; // default/fallback

  const trimToSelectionLimits = createSelectionTrimmer(activeSelectionLimit); // Update selection payloads as side-effect of
  // updating the maximum allowable selections or strategy

  useEffect(() => {
    updateInvalidsAndPayload(collections); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workbook, selectionsStrategy, maxCollections, maxSelections, updatedAt]); // Update the active collection when
  // updating strategy only allows a single
  // collection

  useEffect(() => {
    if (!isMultiCollectionSelection && activeCollection > 0) {
      setActiveCollection(0);
    } // eslint-disable-next-line react-hooks/exhaustive-deps

  }, [isMultiCollectionSelection]);
  const selectAddingNewSelection = useCallback(function () {
    let activate = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : true;
    let collectionIndex = arguments.length > 1 ? arguments[1] : undefined;
    if (activate) setActiveSelection(-1);
    setIsAddingNewSelection(activate);
    if (collectionIndex !== undefined) setActiveCollection(collectionIndex);
  }, []);
  const selectSelection = useCallback(function (index) {
    let isNewCollection = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
    setIsAddingNewSelection(false);
    setActiveSelection(currentIndex => {
      if (isNewCollection) return index;
      return index === currentIndex ? -1 : index;
    });

    if (containerEl !== null && containerEl !== void 0 && containerEl.current) {
      const selectionEl = containerEl.current.querySelector(`[${SELECTION_INDEX_QUERY}="${index}"]`);
      selectionEl && selectionEl.focus();
    }
  }, [containerEl]); // Update the invalid selections,
  // valid, non-publishable, selections,
  // and publishable selection string,

  const updateInvalidsAndPayload = useCallback(updatedCollections => {
    // Update invalid & payload
    const invalids = filterInvalidCollectionSelections(); // Get valid collections, under maximums

    const valids = filterValidCollectionSelections(updatedCollections, invalids).filter(collection => collection.length) // Remove empties
    .slice(0, maxCollections); // Clip outdated selections
    // Get publishable selections

    const publishable = valids.map(collection => collection.slice(0, maxSelections));
    setInvalidSelections(invalids);
    setSelections(encodeSelections(valids)); // user facing

    setPublishableSelections(encodeSelections(publishable)); // system
  }, [maxCollections, maxSelections]); // Update a selection
  // with a new cell reference
  // TODO: Refector this monster 🧟‍♂️

  const changeSelection = useCallback(cellRefs => {
    const sheetIndex = spreadsheetCore.getSpreadsheetIndex(cellRefs);
    const isUpsertingSelection = activeSelection > -1 || isAddingNewSelection;
    if (!isUpsertingSelection) return; // User is not updating/adding

    let selectionsUpdate = [];
    let currentSelections = [];
    let selectionIndexUpdate = -1; // Update collection changed selection

    setCollections(currentCollections => {
      var _currentSelections;

      currentSelections = currentCollections[activeCollection];
      const [firstAddr, lastAddr] = spreadsheetCore.cellReferencesToAddress(cellRefs);
      if (!firstAddr) return currentCollections; // Ignore empty cell references

      const [first, last] = trimToSelectionLimits(firstAddr, lastAddr);
      if (!first) return currentCollections; // Ignore unsalvagable cell reference
      // Ignore duplicate cell reference

      const cellRefsIndex = (_currentSelections = currentSelections) === null || _currentSelections === void 0 ? void 0 : _currentSelections.findIndex(selection => selection === cellRefs);

      if (cellRefsIndex !== -1) {
        selectionIndexUpdate = cellRefsIndex;
        return currentCollections;
      } // Create new selection with trimmed
      // excess cells removed from last address


      const trimmedSelection = spreadsheetCore.prependSheetIndex(`${first.ref}${!last || first.ref === last.ref ? '' : `:${last.ref}`}`, sheetIndex);

      if (activeSelection >= 0) {
        currentSelections[activeSelection] = trimmedSelection;
        selectionsUpdate = [...currentSelections];
      } else {
        // Add new selection
        selectionsUpdate = [...currentSelections, trimmedSelection];
      } // Strip out duplicates & invalids


      selectionsUpdate = selectionsUpdate.filter(Boolean).filter((selection, i, collection) => collection.indexOf(selection) === i); // Apply update

      setUpdatedAt(Date.now());
      currentCollections[activeCollection] = selectionsUpdate;
      return [...currentCollections];
    }); // Check if selection is already active

    if (activeSelection === selectionIndexUpdate && selectionIndexUpdate !== -1) {
      return;
    } // Select an existing selection


    if (selectionIndexUpdate !== -1) {
      selectSelection(selectionIndexUpdate);
      return;
    } // Select the last newly added selection
    // that was jest added to active collection


    if (selectionsUpdate[selectionsUpdate.length - 1] !== currentSelections[currentSelections.length - 1]) {
      selectSelection(selectionsUpdate.length - 1);
    }
  }, [activeCollection, activeSelection, isAddingNewSelection, selectSelection, trimToSelectionLimits, setUpdatedAt]);
  const selectCollection = useCallback(index => {
    setActiveCollection(index);
  }, [setActiveCollection]);
  const removeSelection = useCallback((collectionIndex, selectionIndex) => {
    const collection = collections[collectionIndex];
    collection.splice(selectionIndex, 1);
    setCollections([...collections]);
    setUpdatedAt(Date.now()); // Remove active selection
    // if removing active

    if (activeCollection === collectionIndex && activeSelection === selectionIndex) {
      selectSelection(-1);
    }
  }, [collections, activeCollection, activeSelection, selectSelection]);
  const removeCollection = useCallback(collectionIndex => {
    collections.splice(collectionIndex, 1);
    setCollections([...collections]);
    selectSelection(-1);
    selectCollection(Math.max(0, collectionIndex - 1));
    setUpdatedAt(Date.now());
  }, [collections, selectSelection, selectCollection, setUpdatedAt]);
  const addCollection = useCallback(() => {
    collections.push([]);
    setCollections([...collections]);
    selectCollection(collections.length - 1);
    selectSelection(0); // Reset

    selectAddingNewSelection(true); // auto add
  }, [collections, selectCollection, selectSelection, selectAddingNewSelection]);
  const resetActiveSelection = useCallback(() => {
    setActiveCollection(getInitialCollectionIndex(collections, initialSelectionIndex));
    setActiveSelection(initialSelectionIndex);
  }, [collections, initialSelectionIndex]);
  return {
    selections,
    publishableSelections,
    selectionCollections: collections,
    invalidSelectionCollections: invalidSelections,
    isAddingNewSelection,
    updatedAt,
    hasUpdates,
    selectAddingNewSelection,
    activeCollection,
    activeSelection,
    activeSelectionCellRef,
    selectCollection,
    selectSelection,
    removeSelection,
    removeCollection,
    changeSelection,
    resetActiveSelection,
    addCollection
  };
} // Trim away edges of unacceptable
// user selection until valid
// for the selected section

function createSelectionTrimmer(_ref2) {
  let {
    size = Infinity,
    height = Infinity,
    width = Infinity
  } = _ref2;
  return (first, last) => {
    const firstColIndex = first.col;
    const lastColIndex = last ? last.col : first.col;
    const firstRowIndex = first.row;
    const lastRowIndex = last ? last.row : first.row;
    const refWidth = lastColIndex - firstColIndex + 1;
    const refHeight = lastRowIndex - firstRowIndex + 1;
    const refSize = refWidth * refHeight;
    const result = cloneDeep([first, last].filter(Boolean)); // Selection overall area too large

    if (refSize > size) {
      let currenSize = refSize;
      let rowIndex = lastRowIndex;
      let colIndex = lastColIndex; // Crop edges of last selection,
      // column by row until it fits into
      // the maximum allowable size

      let itteration = 0;

      while (currenSize > size) {
        if (itteration % 2 === 0) {
          colIndex -= colIndex < 1 || colIndex <= firstColIndex ? 0 : 1;
        } else {
          rowIndex -= rowIndex < 1 || rowIndex <= firstRowIndex ? 0 : 1;
        }

        itteration += 1;
        const newWidth = colIndex - firstColIndex + 1;
        const newHeight = rowIndex - firstRowIndex + 1;
        currenSize = newWidth * newHeight;
        if (itteration > 10000) break; // Safety
      } // Reject unsalvagable selection


      if (currenSize > size) {
        return [];
      } // Update the last selection
      // with valid cell reference


      result[1] = spreadsheetCore.coordinatesToAddress(rowIndex, colIndex);
      return result;
    } // Selection too wide


    if (refWidth > width) {
      // Update the last selection
      // with maximum allowable width
      result[1] = spreadsheetCore.coordinatesToAddress(lastRowIndex + 1, firstColIndex + (width - 1));
      return result;
    } // Selection too tall


    if (refHeight > height) {
      // Update the last selection
      // with maximum allowable height
      result[1] = spreadsheetCore.coordinatesToAddress(firstRowIndex + height, lastColIndex);
      return result;
    }

    return result;
  };
} // Lookup all selections that have
// only empty/useless values
// NOTE: #871 allowing empty selections, to make
//       selections more user friendly. Sections
//       should be filtering to what's useful
// TODO: add configuration for numeric visuals


function filterInvalidCollectionSelections() {
  // collections: string[][],
  // workbook: WorkbookData
  return [[]]; // return collections.map((collection) =>
  //   collection.filter((selection) => {
  //     const referenceMatrix =
  //       spreadsheetCore.cellReferencesToAddressMatrix(selection, workbook);
  //     // Test if selection contains
  //     // any non-undefined values
  //     // TODO: check if selection sizes
  //     //       are contained within `activeSelectionLimit`
  //     return referenceMatrix.some((row) =>
  //       row.some((address) => {
  //         const value = ((workbook[address.sheet] || [])[address.row] || [])[
  //           address.col
  //         ];
  //         return (
  //           typeof value === 'undefined' ||
  //           (typeof value === 'string' && value.length === 0)
  //         );
  //       })
  //     );
  //   })
  // );
} // Filter out selections from each
// collection that are not valid


function filterValidCollectionSelections(collections, invalids) {
  return collections.map((collection, collectionIndex) => collection.filter(selection => {
    const invalidCollection = invalids[collectionIndex] || [];
    return !invalidCollection.includes(selection);
  }));
} // Lookup the index of the collection
// that contains the initial selection
// or default to the first collection


function getInitialCollectionIndex(collections, initialSelectionIndex) {
  if (initialSelectionIndex === -1) return 0;
  const initialSelection = flattenDeep(collections)[initialSelectionIndex];
  const idx = collections === null || collections === void 0 ? void 0 : collections.findIndex(selections => selections.includes(initialSelection));
  return idx === -1 ? 0 : idx;
}